import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useBranding } from '../Context/BrandingContext';
import { useLoginModal } from '../Context/LoginModalContext';
import { getSessionToken, getUserDetails } from '../Services/services';
import { ROLES, hasRole } from '../utils/roles';
import {
  getCart,
  removeCartItem,
  clearCart as clearCartApi,
  submitCart,
  updateCartItemQuantity,
  updateCartItemSection,
  addToCart,
} from '../api/inventory';
import { getQuotes, getQuoteLines, deleteQuoteLine } from '../api/quotes';
import { track, logger } from '../utils/logger';
import AppHeader from './AppHeader';
import InventoryDetailsModal from './InventoryDetailsModal';
import PermissionGate from './PermissionGate';
import { isInsufficientPermissionError } from '../utils/permissionErrors';

const QUOTE_TTL_DAYS = 30;
const WARN_DAYS = 10;

function sectionOf(item) {
  return item?.cart_section || 'shopping';
}

function kindOf(item) {
  return item?.catalog_kind || 'parts';
}

// Parts and repair capabilities become different quote line types in Salesforce
// (Outright vs Work Order) and cannot share a quote, so the cart keeps them apart
// wherever both can land. The switch only appears once both kinds are present —
// with a single kind there is nothing to separate.
function splitByKind(list) {
  const parts = list.filter((i) => kindOf(i) === 'parts');
  const repair = list.filter((i) => kindOf(i) === 'repair');
  return { parts, repair, hasBoth: parts.length > 0 && repair.length > 0 };
}

function defaultKindFor(list) {
  return list.some((i) => kindOf(i) === 'parts') || list.length === 0 ? 'parts' : 'repair';
}

function daysRemainingFrom(dateStr) {
  if (!dateStr) return null;
  const created = new Date(dateStr);
  if (Number.isNaN(created.getTime())) return null;
  const expires = new Date(created.getTime() + QUOTE_TTL_DAYS * 24 * 60 * 60 * 1000);
  return Math.ceil((expires.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

function isLineOrdered(l) {
  return l?.Ordered === true || (l?.Status || l?.inscor__Status__c) === 'Accepted';
}

function formatPrice(val) {
  if (val == null) return '—';
  const n = Number(val);
  // Group thousands with commas and always show 2 decimals (e.g. $1,234.50).
  return Number.isNaN(n)
    ? '—'
    : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Mirrors the Tenant API `is_purchase_ready` (cart_service): a part is ready for the
// Shopping Cart only when it has a price, condition, and available qty. Repair items and
// anything missing those go to Request a Quote. Used to route "Move to Cart" from Saved
// for Later to the correct section instead of always dropping items into Shopping.
function isPurchaseReady(item) {
  if ((item?.catalog_kind || 'parts') === 'repair') return false;
  const price = item?.price;
  const condition = (item?.condition || '').trim();
  const qtyAvail = Number(item?.quantity_available);
  const qtyOk = item?.quantity_available != null && !Number.isNaN(qtyAvail) && qtyAvail > 0;
  return price != null && condition !== '' && qtyOk;
}

// Repair items never carry a condition of their own: Salesforce resolves the real
// condition code downstream from the MRO service level (see cart_router.submit_cart).
// Until then the service level is what the shopper chose, so it stands in for the
// condition rather than leaving the column empty.
function conditionLabel(item) {
  if ((item?.catalog_kind || 'parts') !== 'repair') return item?.condition || '—';
  const service = (item?.mro_service || '').trim();
  if (!service) return 'MRO';
  return service.charAt(0).toUpperCase() + service.slice(1);
}

function cartItemSubtitle(item) {
  const desc = (item?.description || '').trim();
  if (desc) return desc;
  if (kindOf(item) === 'repair') return 'Repair Capability';
  return null;
}

function quoteLineSubtitle(line) {
  return (line?.ProductDescription || line?.Description || line?.Keyword || '').trim() || null;
}

function qtyEditMax(item, mode = 'shopping') {
  if (mode === 'rfq' || mode === 'saved') {
    const avail = Number(item?.quantity_available);
    if (!Number.isNaN(avail) && avail > 1) return avail;
    return 999;
  }
  return item?.quantity_available || 999;
}

function canEditCartQty(item, mode = 'shopping') {
  if (kindOf(item) === 'repair') return false;
  if (mode === 'rfq' || mode === 'saved') return true;
  return (item?.quantity_available ?? 2) > 1;
}

function KindSwitch({ activeKind, partsCount, repairCount, primaryColor, onChange }) {
  return (
    <div style={styles.kindSwitch}>
      {[
        { key: 'parts', label: 'Parts', count: partsCount },
        { key: 'repair', label: 'Repair Capabilities', count: repairCount },
      ].map((k) => {
        const active = k.key === activeKind;
        return (
          <button
            key={k.key}
            type="button"
            aria-pressed={active}
            style={{
              ...styles.kindBtn,
              ...(active ? { backgroundColor: primaryColor, color: 'white', borderColor: primaryColor } : {}),
            }}
            onClick={() => onChange(k.key)}
          >
            {k.label} ({k.count})
          </button>
        );
      })}
    </div>
  );
}

export default function Cart() {
  const { branding } = useBranding();
  const { openLogin } = useLoginModal();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(ROLES.GUEST);

  const [shoppingSelected, setShoppingSelected] = useState(new Set());
  const [rfqSelected, setRfqSelected] = useState(new Set());
  const [yourItemsTab, setYourItemsTab] = useState('rfq'); // rfq | quoted | saved
  const [rfqKind, setRfqKind] = useState('parts');     // parts | repair
  const [savedKind, setSavedKind] = useState('parts'); // parts | repair

  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState({});
  const [moving, setMoving] = useState({});
  const [clearing, setClearing] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [showPendingPopup, setShowPendingPopup] = useState(false);
  const [updatingQty, setUpdatingQty] = useState({});
  const [editQty, setEditQty] = useState({});
  const [techDataItem, setTechDataItem] = useState(null);

  // Quoted Items (live from Salesforce Ready-to-Purchase quotes)
  const [quotedLines, setQuotedLines] = useState([]);
  const [quotedLoading, setQuotedLoading] = useState(false);

  const isGuest = !getSessionToken();
  const isStandard = hasRole(userRole, ROLES.STANDARD);

  useEffect(() => {
    if (isGuest) {
      openLogin({ redirectPath: '/cart' });
      return;
    }
    const fetchRole = async () => {
      try {
        const resp = await getUserDetails();
        setUserRole(resp?.data?.role || auth.role || ROLES.PENDING);
      } catch {
        setUserRole(auth.role || ROLES.PENDING);
      }
    };
    fetchRole();
  }, [auth.role, isGuest, openLogin]);

  const fetchCart = useCallback(async () => {
    if (isGuest) return;
    setLoading(true);
    setError('');
    try {
      const data = await getCart();
      const next = data?.items || [];
      setItems(next);
      // Default-select shopping + rfq items. The RFQ default is scoped to one catalog
      // kind so the initial selection can never be a mix.
      const rfq = next.filter((i) => sectionOf(i) === 'rfq');
      const kind = defaultKindFor(rfq);
      setRfqKind(kind);
      setSavedKind(defaultKindFor(next.filter((i) => sectionOf(i) === 'saved_for_later')));
      setShoppingSelected(new Set(next.filter((i) => sectionOf(i) === 'shopping').map((i) => i.id)));
      setRfqSelected(new Set(rfq.filter((i) => kindOf(i) === kind).map((i) => i.id)));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [isGuest]);

  const fetchQuotedItems = useCallback(async () => {
    if (isGuest || !isStandard) return;
    setQuotedLoading(true);
    try {
      const data = await getQuotes({
        page: 1,
        limit: 50,
        status: 'Sent to Customer',
        kind: 'parts',
      });
      const quotes = Array.isArray(data) ? data : (data?.records || data?.quotes || []);
      const rows = [];
      await Promise.all(
        quotes.map(async (q) => {
          try {
            const linesResp = await getQuoteLines(q.Id || q.id, { limit: 200 });
            const lines = Array.isArray(linesResp)
              ? linesResp
              : (linesResp?.lines || linesResp?.records || []);
            const created = q.CreatedDate || q.createdDate || q.inscor__Create_Date__c;
            const daysLeft = daysRemainingFrom(created);
            if (daysLeft != null && daysLeft < 0) return;
            lines.filter((l) => !isLineOrdered(l)).forEach((l) => {
              rows.push({
                quoteId: q.Id || q.id,
                quoteName: q.Name || q.name || q.Id,
                createdDate: created,
                daysLeft,
                line: l,
              });
            });
          } catch {
            // ignore per-quote failures
          }
        })
      );
      setQuotedLines(rows);
    } catch (e) {
      logger.warn('cart.quoted_load_failed', { message: e?.message });
      setQuotedLines([]);
    } finally {
      setQuotedLoading(false);
    }
  }, [isGuest, isStandard]);

  useEffect(() => { fetchCart(); }, [fetchCart]);
  useEffect(() => {
    if (yourItemsTab === 'quoted') fetchQuotedItems();
  }, [yourItemsTab, fetchQuotedItems]);

  const shoppingItems = items.filter((i) => sectionOf(i) === 'shopping');
  const rfqItems = items.filter((i) => sectionOf(i) === 'rfq');
  const savedItems = items.filter((i) => sectionOf(i) === 'saved_for_later');

  const rfqSplit = splitByKind(rfqItems);
  const savedSplit = splitByKind(savedItems);
  // With only one kind present the switch is hidden, so fall back to whichever kind
  // is actually there instead of leaving the user on an empty list.
  const rfqActiveKind = rfqSplit.hasBoth ? rfqKind : defaultKindFor(rfqItems);
  const savedActiveKind = savedSplit.hasBoth ? savedKind : defaultKindFor(savedItems);
  const rfqVisible = rfqActiveKind === 'repair' ? rfqSplit.repair : rfqSplit.parts;
  const savedVisible = savedActiveKind === 'repair' ? savedSplit.repair : savedSplit.parts;
  // Selection is scoped to the visible kind, so a quote request can never mix kinds.
  const rfqSelectedVisible = rfqVisible.filter((i) => rfqSelected.has(i.id));

  const handleRfqKindChange = (kind) => {
    setRfqKind(kind);
    const next = kind === 'repair' ? rfqSplit.repair : rfqSplit.parts;
    setRfqSelected(new Set(next.map((i) => i.id)));
  };

  // A quote line that has already been pulled into the cart (any section) is
  // hidden from the Quoted Items tab so it doesn't appear in both places.
  const cartQuoteLineIds = new Set(items.map((i) => i.sf_quote_line_id).filter(Boolean));
  const visibleQuotedLines = quotedLines.filter((r) => !cartQuoteLineIds.has(r.line?.Id));

  const permissionBlocked = isInsufficientPermissionError(error);

  const shoppingSubtotal = shoppingItems
    .filter((i) => shoppingSelected.has(i.id))
    .reduce((sum, i) => sum + (Number(i.price) || 0) * (Number(i.quantity_requested) || 1), 0);

  const showConfirm = ({ title, message, confirmLabel, confirmColor, onConfirm }) => {
    setConfirmDialog({ title, message, confirmLabel, confirmColor, onConfirm });
  };

  useEffect(() => {
    if (!confirmDialog) return;
    const handler = (e) => { if (e.key === 'Escape') setConfirmDialog(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [confirmDialog]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const toggleInSet = (setter, id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRemove = async (itemId) => {
    setRemoving((prev) => ({ ...prev, [itemId]: true }));
    try {
      await removeCartItem(itemId);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      setShoppingSelected((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
      setRfqSelected((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
      setToast({ type: 'success', message: 'Item removed' });
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (e) {
      setToast({ type: 'error', message: e?.message || 'Failed to remove item' });
    } finally {
      setRemoving((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleMoveSection = async (itemId, section, successMsg) => {
    setMoving((prev) => ({ ...prev, [itemId]: true }));
    try {
      const updated = await updateCartItemSection(itemId, section);
      setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, ...updated } : i)));
      setShoppingSelected((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
      setRfqSelected((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
      if (section === 'shopping') {
        setShoppingSelected((prev) => new Set(prev).add(itemId));
      }
      if (section === 'rfq') {
        setRfqSelected((prev) => new Set(prev).add(itemId));
      }
      setToast({ type: 'success', message: successMsg || 'Item moved' });
      window.dispatchEvent(new Event('cartUpdated'));
      track('cart.section_move', { section });
    } catch (e) {
      setToast({ type: 'error', message: e?.message || 'Failed to move item' });
    } finally {
      setMoving((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleClear = () => {
    showConfirm({
      title: 'Empty Cart',
      message: 'All items will be removed from your cart. This cannot be undone.',
      confirmLabel: 'Empty Cart',
      confirmColor: '#dc2626',
      onConfirm: async () => {
        setClearing(true);
        try {
          await clearCartApi();
          setItems([]);
          setShoppingSelected(new Set());
          setRfqSelected(new Set());
          setToast({ type: 'success', message: 'Cart cleared' });
          window.dispatchEvent(new Event('cartUpdated'));
        } catch (e) {
          setToast({ type: 'error', message: e?.message || 'Failed to clear cart' });
        } finally {
          setClearing(false);
        }
      },
    });
  };

  const handleUpdateQty = async (item, newQty, mode = 'shopping') => {
    const maxQty = qtyEditMax(item, mode);
    const clampedQty = Math.min(maxQty, Math.max(1, Number(newQty) || 1));
    if (clampedQty === item.quantity_requested) return;
    setUpdatingQty((prev) => ({ ...prev, [item.id]: true }));
    try {
      await updateCartItemQuantity(item.id, clampedQty);
      track('cart.qty_update', { qty: clampedQty });
      await fetchCart();
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (e) {
      logger.error('cart.qty_update_failed', { qty: clampedQty, status: e?.status, message: e?.message });
      setToast({ type: 'error', message: e?.message || 'Failed to update quantity' });
    } finally {
      setUpdatingQty((prev) => ({ ...prev, [item.id]: false }));
      setEditQty((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
    }
  };

  const guardedAction = (fn) => {
    if (!isStandard) {
      setShowPendingPopup(true);
      return;
    }
    fn();
  };

  const handleRequestQuote = (ids) => {
    if (!ids.length) return;
    showConfirm({
      title: 'Request a Quote',
      message: `Submit a quote request for ${ids.length} item${ids.length !== 1 ? 's' : ''}?`,
      confirmLabel: 'Request for Quote',
      confirmColor: branding.primaryColor,
      onConfirm: async () => {
        setSubmitting(true);
        track('quote.submit', { lineCount: ids.length, source: 'your_items_rfq' });
        try {
          const result = await submitCart(ids);
          setItems((prev) => prev.filter((i) => !ids.includes(i.id)));
          setRfqSelected((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.delete(id));
            return next;
          });
          const quoteName = result?.quote_name || result?.quote_id || '';
          window.dispatchEvent(new Event('cartUpdated'));
          track('quote.submitted', {
            lineCount: result?.quote_lines_created || ids.length,
            hasQuoteId: !!result?.quote_id,
          });
          setToast({
            type: 'success',
            message: `Quote ${quoteName} submitted — ${result?.quote_lines_created || ids.length} line(s) created`,
          });
          setYourItemsTab('quoted');
        } catch (e) {
          logger.error('quote.submit_failed', { lineCount: ids.length, status: e?.status, message: e?.message });
          setToast({ type: 'error', message: e?.message || 'Quote submission failed' });
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const handleProceedToCheckout = () => {
    const selected = shoppingItems.filter((i) => shoppingSelected.has(i.id));
    if (!selected.length) {
      setToast({ type: 'error', message: 'Select at least one Shopping Cart item to checkout' });
      return;
    }
    guardedAction(() => {
      track('cart.checkout_intent', { lineCount: selected.length });
      navigate('/checkout', {
        state: {
          mode: 'cart',
          cartItemIds: selected.map((i) => i.id),
          cartItems: selected,
        },
      });
    });
  };

  const handleMoveQuotedToCart = async (row) => {
    const l = row.line;
    const key = l.Id || `${row.quoteId}-${Math.random()}`;
    setMoving((prev) => ({ ...prev, [key]: true }));
    try {
      const already = items.find((i) => i.sf_quote_line_id === l.Id);
      if (already) {
        await handleMoveSection(already.id, 'shopping', 'Moved to Shopping Cart');
      } else {
        const created = await addToCart({
          cart_section: 'shopping',
          sf_product_id: l.ProductId || l.inscor__Product__c || null,
          sf_inventory_line_id: l.InventoryLineId || l.inscor__Inventory_Line__c || null,
          part_number: l.ProductName || l.inscor__Product_Name__c || l.inscor__Part_Number__c || '',
          condition: l.Condition || l.inscor__Condition__c || l.inscor__Condition_Code__c?.Name || '',
          description: quoteLineSubtitle(l) || '',
          source_type: 'Quoted',
          quantity_requested: Number(l.Quantity ?? l.inscor__Quantity__c ?? 1) || 1,
          quantity_available: Number(l.Quantity ?? l.inscor__Quantity__c ?? 1) || 1,
          price: Number(l.Price ?? l.inscor__Price__c ?? 0) || null,
          sf_quote_id: row.quoteId,
          sf_quote_line_id: l.Id,
          sf_quote_name: row.quoteName,
          quoted_at: row.createdDate,
        });
        setItems((prev) => [created, ...prev]);
        setShoppingSelected((prev) => new Set(prev).add(created.id));
        setToast({ type: 'success', message: 'Moved to Shopping Cart' });
        window.dispatchEvent(new Event('cartUpdated'));
      }
      track('cart.quoted_move_to_cart', { quoteId: row.quoteId });
    } catch (e) {
      setToast({ type: 'error', message: e?.message || 'Failed to move to cart' });
    } finally {
      setMoving((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Normalizes a quoted Customer Quote Line into the cart-item shape the Tech Data
  // modal consumes, then opens it. The list endpoint now returns the raw
  // Product/Inventory Line Ids used to fetch the part image and full tech data.
  const showQuoteLineTechData = (row) => {
    const l = row.line || {};
    setTechDataItem({
      sf_inventory_line_id: l.InventoryLineId || l.inscor__Inventory_Line__c || null,
      sf_product_id: l.ProductId || l.inscor__Product__c || null,
      part_number: l.ProductName || l.inscor__Product_Name__c || l.inscor__Part_Number__c || '',
      condition: l.Condition || l.inscor__Condition_Code__c?.Name || '',
      quantity_available: l.Quantity ?? l.inscor__Quantity__c,
      price: l.Price ?? l.inscor__Price__c,
      uom: l.UOM || l.inscor__Quoted_UOM__c,
      warehouse_name: '',
    });
  };

  // Tech Data needs at least an Inventory Line (full record) or a Product (part
  // image + basic fields) to show something useful.
  const canShowTechData = (item) => !!(item?.sf_inventory_line_id || item?.sf_product_id);

  const handleDeleteQuotedLine = (row) => {
    const l = row.line;
    const key = l.Id;
    showConfirm({
      title: 'Remove Quoted Item',
      message: 'This will remove the line from your quote. This cannot be undone.',
      confirmLabel: 'Remove',
      confirmColor: '#dc2626',
      onConfirm: async () => {
        setRemoving((prev) => ({ ...prev, [key]: true }));
        try {
          await deleteQuoteLine(row.quoteId, l.Id);
          setQuotedLines((prev) => prev.filter((r) => !(r.quoteId === row.quoteId && r.line.Id === l.Id)));
          setToast({ type: 'success', message: 'Quoted item removed' });
          track('cart.quoted_line_delete', { quoteId: row.quoteId });
        } catch (e) {
          setToast({ type: 'error', message: e?.message || 'Failed to remove quoted item' });
        } finally {
          setRemoving((prev) => ({ ...prev, [key]: false }));
        }
      },
    });
  };

  if (isGuest) {
    return (
      <div style={styles.page}>
        <AppHeader title="Cart" />
        <main style={styles.main}>
          <div style={styles.container}>
            <div style={styles.card}>
              <p style={styles.muted}>Please sign in to view your cart.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const renderQtyCell = (item, mode = 'shopping') => {
    if (!canEditCartQty(item, mode)) return item.quantity_requested;
    const maxQty = qtyEditMax(item, mode);
    if (updatingQty[item.id]) {
      return <span style={{ color: '#6b7280', fontSize: 12 }}>Updating…</span>;
    }
    return (
      <input
        type="number"
        min={1}
        max={maxQty}
        value={editQty[item.id] ?? item.quantity_requested}
        onChange={(e) => setEditQty((prev) => ({ ...prev, [item.id]: e.target.value }))}
        onBlur={(e) => handleUpdateQty(item, e.target.value, mode)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        style={styles.qtyInput}
        title={maxQty < 999 ? `Max available: ${maxQty}` : undefined}
      />
    );
  };

  const TrashIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" width={15} height={15}
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );

  return (
    <div style={styles.page}>
      <AppHeader title="Shopping Cart" />

      {techDataItem && (
        <InventoryDetailsModal
          line={{
            Id: techDataItem.sf_inventory_line_id,
            Condition: techDataItem.condition,
            Quantity: techDataItem.quantity_available,
            Price: techDataItem.price,
            UOM: techDataItem.uom,
            WarehouseName: techDataItem.warehouse_name,
          }}
          partNumber={techDataItem.part_number || ''}
          productId={techDataItem.sf_product_id || null}
          onClose={() => setTechDataItem(null)}
          onAddToCart={null}
          primaryColor={branding.primaryColor}
          cartBusy={{}}
          userRole={userRole}
        />
      )}

      {confirmDialog && (
        <div
          style={styles.popupOverlay}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmDialog(null); }}
        >
          <div style={styles.popupPanel} role="dialog" aria-modal="true">
            <div style={styles.popupHeader}>
              <strong>{confirmDialog.title}</strong>
              <button style={styles.popupClose} onClick={() => setConfirmDialog(null)} aria-label="Close">✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: 'var(--foreground)' }}>
                {confirmDialog.message}
              </p>
            </div>
            <div style={styles.confirmFooter}>
              <button type="button" style={styles.confirmCancelBtn} onClick={() => setConfirmDialog(null)}>
                Cancel
              </button>
              <button
                type="button"
                style={{ ...styles.confirmOkBtn, backgroundColor: confirmDialog.confirmColor }}
                onClick={() => { const fn = confirmDialog.onConfirm; setConfirmDialog(null); fn(); }}
              >
                {confirmDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPendingPopup && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupPanel}>
            <div style={styles.popupHeader}>
              <strong>Account Pending Approval</strong>
              <button style={styles.popupClose} onClick={() => setShowPendingPopup(false)}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.6, color: '#374151' }}>
                Your account is still pending approval. Once approved you can request quotes and checkout.
              </p>
            </div>
            <div style={{ padding: '12px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button style={styles.popupBtn} onClick={() => setShowPendingPopup(false)}>Got It</button>
            </div>
          </div>
        </div>
      )}

      <main style={styles.main}>
        {toast && (
          <div style={{
            ...styles.toast,
            backgroundColor: toast.type === 'success' ? '#059669' : '#dc2626',
          }}>
            {toast.message}
          </div>
        )}

        <div style={styles.container}>
          {userRole === ROLES.PENDING && (
            <div style={styles.pendingBanner}>
              Your account is pending approval. You can organize your cart but cannot submit quotes or checkout until approved.
            </div>
          )}

          <div style={styles.topBar}>
            <Link to="/catalog" style={styles.catalogLink}>← Catalog</Link>
            {items.length > 0 && (
              <button type="button" style={styles.emptyCartBtn} onClick={handleClear} disabled={clearing}>
                {clearing ? 'Emptying…' : 'Empty Cart'}
              </button>
            )}
          </div>

          {permissionBlocked ? (
            <PermissionGate blocked primaryColor={branding.primaryColor} minHeight={400} />
          ) : (
            <div style={styles.layout}>
              <div style={styles.mainCol}>
                {loading && <div style={styles.card}><p>Loading cart…</p></div>}

                {!loading && shoppingItems.length === 0 && rfqItems.length === 0 && savedItems.length === 0 && (
                  <div style={styles.card}>
                    <div style={styles.muted}>
                      Your cart is empty. Browse products in the <Link to="/catalog">catalog</Link>.
                    </div>
                  </div>
                )}

                {/* A — Shopping Cart */}
                {!loading && (
                  <div style={{ ...styles.card, marginBottom: 16 }}>
                    <div style={styles.sectionHeader}>
                      <span style={styles.sectionTitle}>Shopping Cart</span>
                      <span style={styles.sectionCount}>
                        {shoppingItems.length} item{shoppingItems.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {shoppingItems.length === 0 ? (
                      <p style={styles.muted}>
                        Items ready to purchase (price, condition, and quantity available) appear here.
                      </p>
                    ) : (
                      <div style={styles.tableWrap}>
                        <table style={styles.table}>
                          <thead>
                            <tr>
                              <th style={styles.thCheck}>
                                <input
                                  type="checkbox"
                                  style={styles.checkbox}
                                  checked={shoppingItems.length > 0 && shoppingItems.every((i) => shoppingSelected.has(i.id))}
                                  onChange={() => {
                                    const all = shoppingItems.every((i) => shoppingSelected.has(i.id));
                                    setShoppingSelected(all ? new Set() : new Set(shoppingItems.map((i) => i.id)));
                                  }}
                                  aria-label="Select all shopping cart items"
                                />
                              </th>
                              <th style={styles.th}>Part Number</th>
                              <th style={styles.th}>Condition</th>
                              <th style={styles.th}>Qty</th>
                              <th style={styles.th}>Price</th>
                              <th style={styles.th}>Warehouse</th>
                              <th style={styles.th}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {shoppingItems.map((item, idx) => {
                              const busy = removing[item.id] || moving[item.id];
                              return (
                                <tr key={item.id} style={idx % 2 === 0 ? styles.rowEven : undefined}>
                                  <td style={styles.tdCheck}>
                                    <input
                                      type="checkbox"
                                      style={styles.checkbox}
                                      checked={shoppingSelected.has(item.id)}
                                      onChange={() => toggleInSet(setShoppingSelected, item.id)}
                                    />
                                  </td>
                                  <td style={styles.td}>
                                    <div style={styles.partNumber}>{item.part_number}</div>
                                    {cartItemSubtitle(item) && (
                                      <div style={styles.descLine}>{cartItemSubtitle(item)}</div>
                                    )}
                                  </td>
                                  <td style={styles.td}>{item.condition || '—'}</td>
                                  <td style={styles.td}>{renderQtyCell(item)}</td>
                                  <td style={styles.td}>{formatPrice(item.price)}</td>
                                  <td style={styles.td}>{item.warehouse_name || '—'}</td>
                                  <td style={{ ...styles.td, textAlign: 'right' }}>
                                    <div style={styles.cellActions}>
                                      <button
                                        type="button"
                                        style={styles.linkBtn}
                                        disabled={busy}
                                        onClick={() => handleMoveSection(item.id, 'saved_for_later', 'Saved for later')}
                                      >
                                        Save for Later
                                      </button>
                                      {canShowTechData(item) && (
                                        <button type="button" style={styles.techDataBtn} onClick={() => setTechDataItem(item)}>
                                          Tech Data
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        style={{ ...styles.trashBtn, opacity: busy ? 0.4 : 1 }}
                                        onClick={() => handleRemove(item.id)}
                                        disabled={busy}
                                        title="Delete"
                                        aria-label="Delete item"
                                      >
                                        {busy ? '…' : <TrashIcon />}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* B — Your Items */}
                {!loading && (
                  <div style={styles.card}>
                    <div style={styles.sectionHeader}>
                      <span style={styles.sectionTitle}>Your Items</span>
                    </div>
                    <div style={styles.tabRow}>
                      {[
                        { key: 'rfq', label: 'Request a Quote', count: rfqItems.length },
                        { key: 'quoted', label: 'Quoted Items', count: visibleQuotedLines.length },
                        { key: 'saved', label: 'Saved for Later', count: savedItems.length },
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          style={{
                            ...styles.tab,
                            ...(yourItemsTab === tab.key
                              ? { borderBottomColor: branding.primaryColor, color: branding.primaryColor }
                              : {}),
                          }}
                          onClick={() => setYourItemsTab(tab.key)}
                        >
                          {tab.label}
                          {tab.count > 0 && (
                            <span style={{
                              ...styles.tabBadge,
                              backgroundColor: yourItemsTab === tab.key ? branding.primaryColor : '#9ca3af',
                            }}>
                              {tab.count}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* RFQ tab */}
                    {yourItemsTab === 'rfq' && (
                      <>
                        {rfqItems.length === 0 ? (
                          <p style={styles.muted}>
                            Items missing price, condition, or available quantity are added here for quoting.
                          </p>
                        ) : (
                          <>
                            {rfqSplit.hasBoth && (
                              <KindSwitch
                                activeKind={rfqActiveKind}
                                partsCount={rfqSplit.parts.length}
                                repairCount={rfqSplit.repair.length}
                                primaryColor={branding.primaryColor}
                                onChange={handleRfqKindChange}
                              />
                            )}
                            <div style={styles.tableWrap}>
                              <table style={styles.table}>
                                <thead>
                                  <tr>
                                    <th style={styles.thCheck}>
                                      <input
                                        type="checkbox"
                                        style={styles.checkbox}
                                        checked={rfqVisible.length > 0 && rfqVisible.every((i) => rfqSelected.has(i.id))}
                                        onChange={() => {
                                          const all = rfqVisible.every((i) => rfqSelected.has(i.id));
                                          setRfqSelected(all ? new Set() : new Set(rfqVisible.map((i) => i.id)));
                                        }}
                                        aria-label="Select all RFQ items"
                                      />
                                    </th>
                                    <th style={styles.th}>Part Number</th>
                                    <th style={styles.th}>Condition</th>
                                    <th style={styles.th}>Qty</th>
                                    <th style={styles.th}>Price</th>
                                    <th style={styles.th}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rfqVisible.map((item, idx) => {
                                    const busy = removing[item.id] || moving[item.id];
                                    return (
                                      <tr key={item.id} style={idx % 2 === 0 ? styles.rowEven : undefined}>
                                        <td style={styles.tdCheck}>
                                          <input
                                            type="checkbox"
                                            style={styles.checkbox}
                                            checked={rfqSelected.has(item.id)}
                                            onChange={() => toggleInSet(setRfqSelected, item.id)}
                                          />
                                        </td>
                                        <td style={styles.td}>
                                          <div style={styles.partNumber}>{item.part_number}</div>
                                          {cartItemSubtitle(item) && (
                                            <div style={styles.descLine}>{cartItemSubtitle(item)}</div>
                                          )}
                                        </td>
                                        <td style={styles.td}>{conditionLabel(item)}</td>
                                        <td style={styles.td}>{renderQtyCell(item, 'rfq')}</td>
                                        <td style={styles.td}>{formatPrice(item.price)}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>
                                          <div style={styles.cellActions}>
                                            <button
                                              type="button"
                                              style={styles.linkBtn}
                                              disabled={busy}
                                              onClick={() => handleMoveSection(item.id, 'saved_for_later', 'Saved for later')}
                                            >
                                              Save for Later
                                            </button>
                                            {canShowTechData(item) && (
                                              <button type="button" style={styles.techDataBtn} onClick={() => setTechDataItem(item)}>
                                                Tech Data
                                              </button>
                                            )}
                                            <button
                                              type="button"
                                              style={{ ...styles.trashBtn, opacity: busy ? 0.4 : 1 }}
                                              onClick={() => handleRemove(item.id)}
                                              disabled={busy}
                                              title="Delete"
                                            >
                                              {busy ? '…' : <TrashIcon />}
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            <div style={styles.rfqFooter}>
                              <button
                                type="button"
                                style={{ ...styles.submitBtn, backgroundColor: branding.primaryColor }}
                                disabled={submitting || rfqSelectedVisible.length === 0}
                                onClick={() => guardedAction(() => handleRequestQuote(rfqSelectedVisible.map((i) => i.id)))}
                              >
                                {submitting ? 'Submitting…' : `Request for Quote (${rfqSelectedVisible.length})`}
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* Quoted tab */}
                    {yourItemsTab === 'quoted' && (
                      <>
                        {quotedLoading && <p style={styles.muted}>Loading quoted items…</p>}
                        {!quotedLoading && visibleQuotedLines.length === 0 && (
                          <p style={styles.muted}>
                            Quoted items ready to purchase will appear here. Move them to the Shopping Cart to checkout.
                          </p>
                        )}
                        {!quotedLoading && visibleQuotedLines.length > 0 && (
                          <div style={styles.tableWrap}>
                            <table style={styles.table}>
                              <thead>
                                <tr>
                                  <th style={styles.th}>Part Number</th>
                                  <th style={styles.th}>Quote</th>
                                  <th style={styles.th}>Condition</th>
                                  <th style={styles.th}>Qty</th>
                                  <th style={styles.th}>Price</th>
                                  <th style={styles.th}>Valid</th>
                                  <th style={styles.th}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleQuotedLines.map((row, idx) => {
                                  const l = row.line;
                                  const key = l.Id || idx;
                                  const busy = moving[key] || removing[key];
                                  const days = row.daysLeft;
                                  const urgent = days != null && days < WARN_DAYS;
                                  return (
                                    <tr key={key} style={idx % 2 === 0 ? styles.rowEven : undefined}>
                                      <td style={styles.td}>
                                        <div style={styles.partNumber}>
                                          {l.ProductName || l.inscor__Product_Name__c || l.inscor__Part_Number__c || '—'}
                                        </div>
                                        {quoteLineSubtitle(l) && (
                                          <div style={styles.descLine}>{quoteLineSubtitle(l)}</div>
                                        )}
                                      </td>
                                      <td style={styles.td}>{row.quoteName}</td>
                                      <td style={styles.td}>
                                        {l.Condition || l.inscor__Condition_Code__c?.Name || '—'}
                                      </td>
                                      <td style={styles.td}>{l.Quantity ?? l.inscor__Quantity__c ?? '—'}</td>
                                      <td style={styles.td}>{formatPrice(l.Price ?? l.inscor__Price__c)}</td>
                                      <td style={{
                                        ...styles.td,
                                        color: urgent ? '#dc2626' : 'inherit',
                                        fontWeight: urgent ? 700 : 500,
                                      }}>
                                        {days == null ? '—' : `${days} day${days !== 1 ? 's' : ''}`}
                                      </td>
                                      <td style={{ ...styles.td, textAlign: 'right' }}>
                                        <div style={styles.cellActions}>
                                          <button
                                            type="button"
                                            style={styles.linkBtn}
                                            disabled={busy}
                                            onClick={() => handleMoveQuotedToCart(row)}
                                          >
                                            {moving[key] ? '…' : 'Move to Cart'}
                                          </button>
                                          {(l.InventoryLineId || l.inscor__Inventory_Line__c || l.ProductId || l.inscor__Product__c) && (
                                            <button type="button" style={styles.techDataBtn} onClick={() => showQuoteLineTechData(row)}>
                                              Tech Data
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            style={{ ...styles.trashBtn, opacity: busy ? 0.4 : 1 }}
                                            onClick={() => handleDeleteQuotedLine(row)}
                                            disabled={busy}
                                            title="Delete"
                                          >
                                            {removing[key] ? '…' : <TrashIcon />}
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}

                    {/* Saved for Later tab */}
                    {yourItemsTab === 'saved' && (
                      <>
                        {savedItems.length === 0 ? (
                          <p style={styles.muted}>Items you save for later from the Shopping Cart appear here.</p>
                        ) : (
                          <>
                          {savedSplit.hasBoth && (
                            <KindSwitch
                              activeKind={savedActiveKind}
                              partsCount={savedSplit.parts.length}
                              repairCount={savedSplit.repair.length}
                              primaryColor={branding.primaryColor}
                              onChange={setSavedKind}
                            />
                          )}
                          <div style={styles.tableWrap}>
                            <table style={styles.table}>
                              <thead>
                                <tr>
                                  <th style={styles.th}>Part Number</th>
                                  <th style={styles.th}>Condition</th>
                                  <th style={styles.th}>Qty</th>
                                  <th style={styles.th}>Price</th>
                                  <th style={styles.th}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {savedVisible.map((item, idx) => {
                                  const busy = removing[item.id] || moving[item.id];
                                  return (
                                    <tr key={item.id} style={idx % 2 === 0 ? styles.rowEven : undefined}>
                                      <td style={styles.td}>
                                        <div style={styles.partNumber}>{item.part_number}</div>
                                        {cartItemSubtitle(item) && (
                                          <div style={styles.descLine}>{cartItemSubtitle(item)}</div>
                                        )}
                                      </td>
                                      <td style={styles.td}>{conditionLabel(item)}</td>
                                      <td style={styles.td}>{renderQtyCell(item, 'saved')}</td>
                                      <td style={styles.td}>{formatPrice(item.price)}</td>
                                      <td style={{ ...styles.td, textAlign: 'right' }}>
                                        <div style={styles.cellActions}>
                                          <button
                                            type="button"
                                            style={styles.linkBtn}
                                            disabled={busy}
                                            onClick={() => {
                                              const ready = isPurchaseReady(item);
                                              handleMoveSection(
                                                item.id,
                                                ready ? 'shopping' : 'rfq',
                                                ready ? 'Moved to Shopping Cart' : 'Moved to Request a Quote'
                                              );
                                            }}
                                          >
                                            Move to Cart
                                          </button>
                                          {canShowTechData(item) && (
                                            <button type="button" style={styles.techDataBtn} onClick={() => setTechDataItem(item)}>
                                              Tech Data
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            style={{ ...styles.trashBtn, opacity: busy ? 0.4 : 1 }}
                                            onClick={() => handleRemove(item.id)}
                                            disabled={busy}
                                            title="Delete"
                                          >
                                            {busy ? '…' : <TrashIcon />}
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* C — Proceed to Checkout panel */}
              <aside style={styles.sideCol}>
                <div style={styles.checkoutCard}>
                  <h3 style={styles.checkoutTitle}>Proceed to Checkout</h3>
                  <div style={styles.checkoutRow}>
                    <span>Selected items</span>
                    <strong>{[...shoppingSelected].filter((id) => shoppingItems.some((i) => i.id === id)).length}</strong>
                  </div>
                  <div style={styles.checkoutRow}>
                    <span>Subtotal</span>
                    <strong>{formatPrice(shoppingSubtotal)}</strong>
                  </div>
                  <p style={styles.checkoutHint}>
                    Only Shopping Cart items with price, condition, and available quantity can be purchased.
                  </p>
                  <button
                    type="button"
                    style={{
                      ...styles.checkoutBtn,
                      backgroundColor: branding.primaryColor,
                      opacity: shoppingSelected.size === 0 ? 0.5 : 1,
                    }}
                    disabled={shoppingSelected.size === 0}
                    onClick={handleProceedToCheckout}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </aside>
            </div>
          )}

          {error && !permissionBlocked && (
            <p style={styles.err}>{error.message || 'Failed to load cart'}</p>
          )}
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: 'var(--background)', color: 'var(--foreground)' },
  main: { padding: '24px 24px' },
  container: { maxWidth: 1200, margin: '0 auto' },
  layout: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 20,
    alignItems: 'flex-start',
  },
  mainCol: { flex: '1 1 560px', minWidth: 0 },
  sideCol: { flex: '0 1 280px', position: 'sticky', top: 88 },
  topBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: 12, marginBottom: 16, flexWrap: 'wrap',
  },
  pendingBanner: {
    backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
    borderRadius: 8, padding: '12px 16px', fontSize: 14, fontWeight: 500, marginBottom: 16,
  },
  card: {
    backgroundColor: 'var(--card)', border: '1px solid var(--border)',
    borderRadius: 12, padding: 20,
  },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 },
  sectionTitle: { fontWeight: 800, fontSize: 16 },
  sectionCount: {
    fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)',
    backgroundColor: 'var(--muted)', borderRadius: 999, padding: '2px 9px',
  },
  err: { color: 'var(--destructive)', fontSize: 14 },
  muted: { color: 'var(--muted-foreground)', fontSize: 14 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  thCheck: {
    padding: '10px 8px 10px 12px', backgroundColor: 'var(--muted)',
    borderBottom: '2px solid var(--border)', width: 36,
  },
  tdCheck: { padding: '10px 8px 10px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle', width: 36 },
  checkbox: { width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--primary)' },
  th: {
    textAlign: 'left', padding: '10px 12px', backgroundColor: 'var(--muted)',
    fontWeight: 600, fontSize: 13, borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap',
  },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  rowEven: { backgroundColor: 'var(--secondary)' },
  partNumber: { fontWeight: 700, fontSize: 13 },
  descLine: { color: 'var(--muted-foreground)', fontSize: 12, marginTop: 2 },
  qtyInput: {
    width: 60, height: 28, borderRadius: 6, border: '1px solid var(--border)',
    textAlign: 'center', fontSize: 13, fontWeight: 600,
  },
  techDataBtn: {
    background: 'transparent', border: '1px solid #d1d5db', color: '#374151',
    borderRadius: 6, padding: '4px 9px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  linkBtn: {
    background: 'transparent', border: '1px solid #d1d5db', color: '#111827',
    borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  trashBtn: {
    background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6,
    width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    color: '#6b7280', cursor: 'pointer', flexShrink: 0,
  },
  cellActions: { display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' },
  catalogLink: {
    fontSize: 13, fontWeight: 600, color: '#374151', textDecoration: 'none', whiteSpace: 'nowrap',
    backgroundColor: '#ffffff', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 16px', display: 'inline-block',
  },
  emptyCartBtn: {
    backgroundColor: '#ffffff', border: '1px solid #d1d5db', color: '#374151',
    borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
  },
  tabRow: { display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 16 },
  tab: {
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    padding: '10px 14px', fontSize: 13, fontWeight: 600, color: '#6b7280', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  },
  tabBadge: {
    color: 'white', borderRadius: 999, fontSize: 11, fontWeight: 700,
    minWidth: 18, height: 18, padding: '0 6px', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center',
  },
  kindSwitch: { display: 'flex', gap: 8, marginBottom: 14 },
  kindBtn: {
    background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 999,
    padding: '6px 14px', fontSize: 12, fontWeight: 700, color: '#6b7280', cursor: 'pointer',
  },
  rfqFooter: { marginTop: 16, display: 'flex', justifyContent: 'flex-end' },
  submitBtn: {
    color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px',
    fontWeight: 600, cursor: 'pointer', fontSize: 13,
  },
  checkoutCard: {
    backgroundColor: 'var(--card)', border: '1px solid var(--border)',
    borderRadius: 12, padding: 20, boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
  },
  checkoutTitle: { margin: '0 0 16px', fontSize: 16, fontWeight: 800 },
  checkoutRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: 14, marginBottom: 10,
  },
  checkoutHint: { fontSize: 12, color: 'var(--muted-foreground)', lineHeight: 1.45, margin: '12px 0 16px' },
  checkoutBtn: {
    width: '100%', color: 'white', border: 'none', borderRadius: 8,
    padding: '12px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 14,
  },
  popupOverlay: {
    position: 'fixed', inset: 0, zIndex: 900, backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  popupPanel: {
    backgroundColor: 'var(--card)', borderRadius: 14, width: '100%', maxWidth: 420,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden', border: '1px solid var(--border)',
  },
  popupHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 24px', borderBottom: '1px solid var(--border)', fontSize: 16, fontWeight: 700,
  },
  popupClose: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--muted-foreground)' },
  popupBtn: {
    backgroundColor: '#111827', color: 'white', border: 'none', borderRadius: 8,
    padding: '8px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  confirmFooter: {
    padding: '12px 24px', borderTop: '1px solid var(--border)',
    display: 'flex', justifyContent: 'flex-end', gap: 8,
  },
  confirmCancelBtn: {
    backgroundColor: 'var(--secondary)', color: 'var(--foreground)',
    border: '1px solid var(--border)', borderRadius: 8,
    padding: '8px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  confirmOkBtn: {
    color: 'white', border: 'none', borderRadius: 8,
    padding: '8px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  toast: {
    position: 'fixed', top: 72, right: 24, color: 'white', padding: '10px 20px',
    borderRadius: 8, fontSize: 14, fontWeight: 500, zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
};
